import {
    isAiReportsModuleActive,
    isFinanceModuleActive,
    isPurchasedOrgMarketplaceModule,
    isSecretaryModuleActive,
} from "@/domains/marketplace/marketplaceModuleSlugs";
import {
    hasActivePaidClinicBilling,
    hasFutureMivoTeamClinicExtension,
    isClinicManager,
} from "@/domains/multi/clinicMemberPaths";
import { hasClinicFinanceRole } from "@/domains/multi/clinicRoles";
import {
    secretaryHasAiReportsAccess,
    secretaryHasFinanceMenu,
    secretaryHasPatientTabView,
} from "@/domains/shared/services/secretaryPermissions";
import type { IUser } from "@/domains/shared/models/IUser";
import { Roles } from "@/domains/shared/models/roles";
import { isFreePlan, isPaidPlan, normalizePlanSlug, SubscriptionPlanIds } from "@/domains/subscription/plans";

export function clinicalAreaBasePath(user: Pick<IUser, "role"> | null | undefined): "/administrativo" | "/profissional" {
    return user?.role === Roles.Secretary ? "/administrativo" : "/profissional";
}

/** Subscription plan used for patient tab gates (files, reports). Secretary uses titular plan. */
export function resolveClinicalSubscriptionPlan(user: IUser | null | undefined): string | null | undefined {
    if (user?.role === Roles.Secretary) {
        return user.linkedTherapistSubscriptionPlan;
    }

    return user?.subscriptionPlan;
}

export function isActiveMultiClinicMember(user: IUser | null | undefined): boolean {
    if (!user?.activeClinicId) {
        return false;
    }

    if (hasActivePaidClinicBilling(user)) {
        return true;
    }

    return Boolean(
        user.clinicMemberships?.some(
            (membership) => membership.isActive && membership.clinicId === user.activeClinicId,
        ),
    );
}

export function resolveFinanceModuleActive(user: IUser | null | undefined): boolean {
    if (user?.role === Roles.Secretary) {
        return secretaryHasFinanceMenu(user.secretaryPermissions);
    }

    if (isFinanceModuleActive(user?.modules)) {
        return true;
    }

    if (
        user?.activeClinicId
        && hasActivePaidClinicBilling(user)
        && (hasClinicFinanceRole(user) || isClinicManager(user))
    ) {
        return true;
    }

    if (user?.role === Roles.Therapist && !isFreePlan(user.subscriptionPlan)) {
        const slug = normalizePlanSlug(user.subscriptionPlan);
        const soloBundledFinance =
            slug === SubscriptionPlanIds.basic || slug === SubscriptionPlanIds.pro;
        const onActiveOrgBilling = Boolean(user.activeClinicId && hasActivePaidClinicBilling(user));

        if (soloBundledFinance && (!user.activeClinicId || !onActiveOrgBilling) && (!user.activeClinicId || isClinicManager(user))) {
            return true;
        }
    }

    return false;
}

/** Patient Arquivos tab: Pro/Clinic solo plan or active Multi org member (storage per professional). */
export function resolvePatientFilesTabEnabled(user: IUser | null | undefined): boolean {
    if (isPaidPlan(resolveClinicalSubscriptionPlan(user))) {
        return true;
    }

    return isActiveMultiClinicMember(user);
}

/** Novo paciente clinic billing: active org subscription or purchased org Finance — not membership alone. */
export function resolveCreatePatientClinicBillingActive(user: IUser | null | undefined): boolean {
    if (!user?.activeClinicId) {
        return false;
    }

    if (hasActivePaidClinicBilling(user)) {
        return true;
    }

    return isFinanceModuleActive(user.modules);
}

/** Novo paciente: show and require session price for clinic create billing or Pro/Clinic solo plan. */
export function resolveCreatePatientSessionPriceGate(user: IUser | null | undefined): {
    show: boolean;
    required: boolean;
} {
    const active =
        resolveCreatePatientClinicBillingActive(user) || isPaidPlan(resolveClinicalSubscriptionPlan(user));
    return { show: active, required: active };
}

/** Contrato tab: Multi members always; solo when paid plan or finance module active. */
export function resolvePatientBillingConfigActive(user: IUser | null | undefined): boolean {
    if (isActiveMultiClinicMember(user)) {
        return true;
    }

    if (isPaidPlan(resolveClinicalSubscriptionPlan(user))) {
        return true;
    }

    return isFinanceModuleActive(user?.modules);
}

/** Secretary module: solo plan/entitlement or bundled Multi clinic subscription. */
export function resolveSecretaryModuleActive(
    user: Pick<IUser, "modules" | "activeClinicId" | "clinic"> | null | undefined,
): boolean {
    if (isSecretaryModuleActive(user?.modules)) {
        return true;
    }

    return Boolean(user?.activeClinicId && hasActivePaidClinicBilling(user));
}

/** Org team chat (`clinicTeamGroup`): paid Multi billing + active membership on session org. */
export function resolveOrgTeamChatActive(user: IUser | null | undefined): boolean {
    if (!user?.activeClinicId) {
        return false;
    }

    const orgEntitled =
        hasActivePaidClinicBilling(user) || hasFutureMivoTeamClinicExtension(user);
    if (!orgEntitled) {
        return false;
    }

    return Boolean(
        user.clinicMemberships?.some(
            (membership) => membership.isActive && membership.clinicId === user.activeClinicId,
        ),
    );
}

/** Team chat: solo Time administrativo module or paid org membership (Multi 2.0.2). */
export function resolveTeamChatActive(user: IUser | null | undefined): boolean {
    if (user?.role === Roles.Secretary) {
        if (user.secretaryAccessBlocked === true) {
            return false;
        }

        if (user.isClinicSecretary) {
            return resolveOrgTeamChatActive(user);
        }

        return true;
    }

    if (resolveSecretaryModuleActive(user)) {
        return true;
    }

    return resolveOrgTeamChatActive(user);
}

export function resolveAiReportsModuleActive(user: IUser | null | undefined): boolean {
    if (user?.role === Roles.Secretary) {
        if (user.isClinicSecretary) {
            return (
                secretaryHasPatientTabView(user.secretaryPermissions, "reports")
                && isPurchasedOrgMarketplaceModule(user.modules, "aiReports")
            );
        }

        return secretaryHasAiReportsAccess(user.secretaryPermissions);
    }

    if (user?.activeClinicId) {
        return isPurchasedOrgMarketplaceModule(user.modules, "aiReports");
    }

    return isAiReportsModuleActive(user?.modules);
}

/** Reception presence (chegada). Secretary: titular module gate via auth/me (no `modules` on secretary). */
export function resolvePresenceModuleActive(user: IUser | null | undefined): boolean {
    if (user?.role === Roles.Secretary) {
        return user.secretaryAccessBlocked !== true;
    }

    return resolveSecretaryModuleActive(user);
}
